<template>
    <div class="ap-ci">
        <div class="ap-ci__container">
            <div class="ap-ci__input-container">
                <h4 class="ap-ci__title" v-text="$ml.get('titleViewApplePay')" tabindex="0" />
                <CieloSelectV2
                    @change="selectItem"
                    :title="$ml.get('installment')"
                    name="selectInstallment"
                    :label="$ml.get('installmentsNumber')"
                    :option-items="applePayInstallmentList || undefined"
                    :selected-item="numberOfInstallments"
                    :on-get-item-event="getLangOptions"
                    :disabled="!applePayInstallmentList.length || this.installmentIsFixed"
                />
            </div>
          
            <div class="ap-ci__input-container">
                <h4 class="ap-ci__title" v-text="$ml.get('yourDocumentNumber')" tabindex="0" />
                <div class="ap-ci__identity-container">
                    <CieloSelectV2
                        @change="selectDocumentType"
                        :title="$ml.get('documentType')"
                        :label="$ml.get('selectDocumentType')"
                        :option-items="documentTypes"
                        :selected-item="selectedDocumentType"
                        v-model="IdentityType"
                        other-class="cielo-select__container--documentType"
                        :can-translate="true"
                    />

                    <CieloInputV2
                        style="width: 100%; margin-bottom: 0px"
                        :autofocus="false"
                        :borderless="false"
                        name="Identity"
                        :label="$ml.get('identity')"
                        v-model="Identity"
                        v-validate="isNational ? 'required|numeric|min:11|max:11|cpf-cnpj' : 'required'"
                        :data-vv-as="$ml.get('identity')"
                        :type="isNational ? 'tel' : 'text'"
                        :error="errors.first('Identity')"
                        :mask="isNational ? ['###.###.###-##'] : ''"
                    />
                </div>
            </div>

            <CieloCheckBoxV2
                v-model="agreement"
                :clickable-slot="true"
                :text-aria="$ml.get('termsPreffix') + $ml.get('termsSuffix')"
            >
                <span v-text="$ml.get('termsPreffix')" />&nbsp;
                <a target="_blank" :href="showTerms" v-text="$ml.get('termsSuffix')" tabindex="0" />
            </CieloCheckBoxV2>
        </div>

        <div class="ap-ci__cta-container">
            <ButtonsFinishPaymentV2
                :disable-button-next="disableButtonFinish"
                :loading="loading"
                @click-next="finishPayment"
                @click-previous="goToPreviousStep"
            />
        </div>
    </div>
</template>

<script>
    const CieloInputV2 = () =>
        import(
            /* webpackChunkName: "cielo-input" */ '@/components/ClickToPay/template/CieloInputV2'
        );

    import Swal from 'sweetalert2';
    import vars from '@/config/vars';
    import CpfCnpjValidation from '@/helpers/validation-rules/CpfCnpjValidation';
    import PaymentProcessor from '@/services/payment/PaymentProcessor';
    import { mapGetters } from 'vuex';
    import { Token, Options } from '@/config/lang/genericOptions.js';
    import { logEvent, logException } from '@/helpers/cieloLogger';
    import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
    import CieloCheckBoxV2 from '@/components/ClickToPay/template/CieloCheckBoxV2';
    import ButtonsFinishPaymentV2 from '@/components/template/ButtonsFinishPaymentV2';

    export default {
        components: {
            CieloSelectV2,
            CieloInputV2,
            CieloCheckBoxV2,
            ButtonsFinishPaymentV2
        },

        data() {
            return {
                loading: false,
                agreement: false,
                showTerms: `${vars.CIELO_CHECKOUT_URL}/TransactionalVNext/Content/documents/Termos%20e%20condicoes%20de%20Uso_Cielo.pdf`,
                documentTypes: [
                    { Text: 'cpf', Value: 'CPF' },
                    { Text: 'cnpj', Value: 'CNPJ' },
                    { Text: 'foreign', Value: 'FOREIGN' },
                ],
                selectedDocumentType: this.IdentityType || 'CPF',
            };
        },

        computed: {
            ...mapGetters({
                applePayInstallmentList: 'payment/getApplePayInstallmentList',
                isNational: 'billingAddress/getIsNational',
                installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed'
            }),

            numberOfInstallments: {
                get: (self) =>
                    self.$store.getters['payment/getInstallmentNumberOfPayments'],
                set(value) {
                    this.$store.commit('payment/setNumberOfInstallments', value);
                },
            },
            Identity: {
                get: (self) => self.$store.state.customer.Identity,
                set(value) {
                    this.$store.commit('customer/setIdentity', value);
                },
            },
            IdentityType: {
                get: (self) => self.$store.state.customer.IdentityType,
                set(value) {
                    this.$store.commit('customer/setIdentityType', value);
                },
            },
            rememberMeAgreement: {
                get: self => self.$store.state.clickToPayPayment.rememberMeAgreement,
                set(value) { this.$store.state.clickToPayPayment.rememberMeAgreement = value; }
            },

            disableButtonFinish() {
                const hasErrors = this.errors.items.length > 0;
                const missingCommonFields = !this.Identity || !this.agreement || !this.numberOfInstallments;
                
                return missingCommonFields || hasErrors;
            }
        },

        created() {
            this.$validator.extend('cpf-cnpj', CpfCnpjValidation);
            PaymentProcessor.multiLanguage = this.$ml;
        },

        methods: {
            goToPreviousStep() {
                logEvent({
                    category: 'apple_pay',
                    action: 'troca_forma_pagamento_apay',
                    label: 'Na tela de escolha de parcela usuario escolhe trocar a forma de pagamento.'
                });

                this.$store.commit('payment/setNumberOfInstallments', 0);
                this.$store.dispatch('pipelineV2/pushTo', 'ChangePaymentType');
            },

            selectDocumentType(value) {
                this.selectedDocumentType = value;
                this.IdentityType = value;
                const type = value === 'FOREIGN' ? 'foreign' : 'national';
			    this.$store.commit('billingAddress/setType', type);
            },

            async finishPayment() {
                if(!this.loading) {
                    this.loading = true;

                    logEvent({
                        category: 'apple_pay',
                        action: 'abre_modal_apay',
                        label: 'Usuário define quantidade de parcela, CPF e clica no botão de finalizar compra abrindo o modal da Apple.'
                    });

                    try {
                        await this.$store.dispatch('applePayPayment/initApplePaySession');
                        await PaymentProcessor.Process();
                    } catch (error) {
                        if(error) {
                            const messageError = error.message;
                            logException(error);
                            console.error(':: Finish Payment Error ::', messageError);
                            Swal.fire({
                                type: 'error',
                                title: this.$ml.get('applePayErrorTitle'),
                                text: this.$ml.get(messageError)
                            });
                        }
                    } finally {
                        this.loading = false;
                    }
                }
            },
            

            selectItem(item) {
                this.numberOfInstallments = item;
            },

            getLangOptions(item) {
                let tokens = [];

                tokens.push(new Token('quantity', item.Value));
                tokens.push(new Token('installmentPrice', item.installmentPrice));

                return new Options('installmentsItem', tokens);
            },
        },
    };
</script>
<style lang='scss' scoped>
    .ap-ci {
        @media screen and (max-width: 1024px) {
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
        }

        &__container {
            margin-bottom: 32px;

            @media screen and (max-width: 1024px) {
                flex: 1;
            }
        }

        &__input-container {
            margin-bottom: 32px;
        }

        &__identity-container {
            display: flex;
			gap: 20px;
			width: 100%;
            
			@media screen and (max-width: 1024px) {
				flex-direction: column;
				margin-top: 0px;
			}
        }

        &__title {
            font-size: 16px;
            font-weight: 700;
            line-height: 11px;
            color: #2F363E;
            margin-bottom: 24px
        }

        &__select-title {
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 11px;
            color: #2F363E;
            margin-bottom: 20px;
        }
    }
</style>
